import { useMemo } from 'react'
import { useLaunchPromoCountdown } from '../hooks/useLaunchPromoCountdown'
import { padCountdownUnit } from '../lib/promo/launchPromo'
import { useT } from '../i18n/t'

type LaunchPromoCountdownProps = {
  variant?: 'bar' | 'inline' | 'panel'
  /** Na ciemnym tle (hero) — jasny/licznik brand zamiast text-ink. */
  tone?: 'light' | 'dark'
  className?: string
}

export function LaunchPromoCountdown({
  variant = 'inline',
  tone = 'light',
  className = '',
}: LaunchPromoCountdownProps) {
  const { t } = useT()
  const { active, remainingMs, parts } = useLaunchPromoCountdown()

  const display = useMemo(() => {
    const m = padCountdownUnit(parts.minutes)
    const s = padCountdownUnit(parts.seconds)
    // Pasek na górze pokazujemy zawsze w formacie HH:MM:SS (do 72h), żeby pasowało
    // do rolującego się licznika 72h. Pozostałe warianty mogą używać formatu z dniami.
    if (variant === 'bar') {
      const totalHours = parts.days * 24 + parts.hours
      return `${padCountdownUnit(totalHours)}:${m}:${s}`
    }
    const h = padCountdownUnit(parts.hours)
    if (parts.days > 0) {
      return `${parts.days}${t('promo.countdown.daySuffix')} ${h}:${m}:${s}`
    }
    return `${h}:${m}:${s}`
  }, [parts.days, parts.hours, parts.minutes, parts.seconds, t, variant])

  const ariaLabel = useMemo(() => {
    const segments: string[] = []
    if (parts.days > 0) {
      segments.push(t('promo.countdown.ariaDays', { count: parts.days }))
    }
    segments.push(
      t('promo.countdown.ariaHms', {
        hours: parts.hours,
        minutes: parts.minutes,
        seconds: parts.seconds,
      }),
    )
    return segments.join(', ')
  }, [parts.days, parts.hours, parts.minutes, parts.seconds, t])

  if (!active || remainingMs <= 0) return null

  const colorClass = tone === 'dark' ? 'text-brand' : 'text-ink'
  const variantClass =
    variant === 'bar'
      ? `font-mono text-[13px] font-bold tabular-nums tracking-wide ${colorClass}`
      : variant === 'panel'
        ? `font-mono text-[28px] font-semibold tabular-nums tracking-tight ${colorClass} md:text-[36px]`
        : `font-mono text-[15px] font-semibold tabular-nums tracking-wide ${colorClass} md:text-[16px]`

  return (
    <time
      dateTime={`PT${parts.days}DT${parts.hours}H${parts.minutes}M${parts.seconds}S`}
      className={[variantClass, className].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
    >
      {display}
    </time>
  )
}
